(() => {
  if (!customElements.get('lumi-brand-film')) customElements.define('lumi-brand-film', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return; this.initialized = true;
      const button = this.querySelector('button'), video = this.querySelector('video'), error = this.querySelector('[role="status"]');
      if (!button || !video) return;
      const update = () => { button.firstChild.textContent = video.paused ? 'Play brand animation ' : 'Pause brand animation '; };
      button.addEventListener('click', async () => {
        video.hidden = false; button.setAttribute('aria-expanded', 'true'); error.hidden = true;
        if (!video.paused) video.pause(); else try { await video.play(); } catch (_) { error.hidden = false; }
        update();
      });
      video.addEventListener('play', update); video.addEventListener('pause', update); video.addEventListener('ended', update);
    }
  });
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const instances = new Map();
  const allowed = el => !motion.matches && !el.closest('[data-lumi-motion="false"]');
  const bindDepth = el => {
    if (instances.has(el)) return;
    const controller = new AbortController();
    let frame = 0, rect = null, point = null;
    const reset = () => {
      cancelAnimationFrame(frame); frame = 0; point = null; rect = null;
      el.classList.remove('lumi-depth-active');
      ['--lumi-rx', '--lumi-ry', '--lumi-z'].forEach(key => el.style.removeProperty(key));
    };
    const enter = event => {
      if (!allowed(el) || !finePointer.matches || event.pointerType === 'touch') return;
      rect = el.getBoundingClientRect();
    };
    const move = event => {
      if (!allowed(el) || !finePointer.matches || event.pointerType === 'touch') { reset(); return; }
      if (!rect) rect = el.getBoundingClientRect();
      point = { x: event.clientX, y: event.clientY };
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!point || !rect || !rect.width || !rect.height || !allowed(el)) return;
        const clamp = n => Math.max(-1, Math.min(1, n));
        const x = clamp((point.x - rect.left) / rect.width * 2 - 1);
        const y = clamp((point.y - rect.top) / rect.height * 2 - 1);
        const depth = Math.max(0, Math.min(4, Number(el.dataset.lumiDepth) || 0));
        el.style.setProperty('--lumi-rx', `${(-y * depth).toFixed(2)}deg`);
        el.style.setProperty('--lumi-ry', `${(x * depth).toFixed(2)}deg`);
        el.style.setProperty('--lumi-z', `${depth * 2}px`);
        el.classList.add('lumi-depth-active');
      });
    };
    for (const [type, handler] of [['pointerenter', enter], ['pointermove', move], ['pointerleave', reset], ['pointercancel', reset], ['blur', reset]]) {
      el.addEventListener(type, handler, { signal: controller.signal, passive: true });
    }
    instances.set(el, { reset, dispose: () => { reset(); controller.abort(); } });
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) {
      if (allowed(entry.target)) entry.target.classList.add('lumi-reveal');
      observer.unobserve(entry.target);
    }
  }), { threshold: .12 }) : null;
  const init = (root = document) => {
    root.querySelectorAll('[data-lumi-depth]').forEach(bindDepth);
    root.querySelectorAll('.lumi-product, .lumi-brand__copy, .lumi-section-heading').forEach(el => {
      if (allowed(el)) observer?.observe(el);
    });
    root.querySelectorAll('.lumi-hero').forEach(el => {
      if (allowed(el)) el.classList.add('lumi-intro');
    });
  };
  const resetDepth = () => instances.forEach(instance => instance.reset());
  motion.addEventListener('change', resetDepth);
  finePointer.addEventListener('change', resetDepth);
  window.addEventListener('resize', resetDepth, { passive: true });
  document.addEventListener('visibilitychange', () => { if (document.hidden) resetDepth(); });
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, el) => {
      if (event.target.contains(el)) { instance.dispose(); instances.delete(el); }
    });
    event.target.querySelectorAll('.lumi-product, .lumi-brand__copy, .lumi-section-heading').forEach(el => observer?.unobserve(el));
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  init();
})();

// Cinematic image illumination enhances an already visible server-rendered page.
(() => {
  const scenes = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

  const setup = (root = document) => root.querySelectorAll('[data-lumi-scene]').forEach(scene => {
    if (scenes.has(scene)) return;
    const controller = new AbortController();
    let frame = 0;
    const pause = scene.querySelector('[data-lumi-pause]');
    const motionAllowed = () => !reduced.matches && scene.dataset.lumiMotion !== 'false';
    const reset = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      scene.classList.remove('spotlight-active');
      scene.style.removeProperty('--spot-x');
      scene.style.removeProperty('--spot-y');
      scene.style.removeProperty('--depth-x');
      scene.style.removeProperty('--depth-y');
    };

    if (pause && motionAllowed()) {
      pause.hidden = false;
      pause.addEventListener('click', () => {
        const stopped = scene.toggleAttribute('data-lumi-paused');
        pause.setAttribute('aria-pressed', String(stopped));
        pause.textContent = stopped ? 'Resume light animation' : 'Pause light animation';
      }, { signal: controller.signal });
    }

    scene.addEventListener('pointermove', event => {
      if (!motionAllowed() || !finePointer.matches || event.pointerType === 'touch') return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = scene.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 100));
        const y = Math.max(0, Math.min(100, (event.clientY - rect.top) / rect.height * 100));
        scene.style.setProperty('--spot-x', `${x.toFixed(2)}%`);
        scene.style.setProperty('--spot-y', `${y.toFixed(2)}%`);
        scene.style.setProperty('--depth-x', `${((x - 50) / 50 * 0.8).toFixed(2)}deg`);
        scene.style.setProperty('--depth-y', `${((50 - y) / 50 * 0.6).toFixed(2)}deg`);
        scene.classList.add('spotlight-active');
      });
    }, { signal: controller.signal, passive: true });
    scene.addEventListener('pointerleave', reset, { signal: controller.signal, passive: true });
    scene.addEventListener('pointercancel', reset, { signal: controller.signal, passive: true });
    scenes.set(scene, { controller, reset });
  });

  const unload = event => scenes.forEach((instance, scene) => {
    if (event.target.contains(scene)) {
      instance.reset();
      instance.controller.abort();
      scenes.delete(scene);
    }
  });
  const resetAll = () => scenes.forEach(instance => instance.reset());
  reduced.addEventListener('change', resetAll);
  finePointer.addEventListener('change', resetAll);
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', unload);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup());
  else setup();
})();
